import { addHoursToTimestamp, formatDate } from "shared-utils";
import { CrewList } from "../types/CrewList";
import { JobDatesCrewList } from "../types/JobDateCrewList";
import { addHours } from 'date-fns';
import { mapDecimalToString } from "shared-utils";
import { formatLocalTime } from "shared-utils";

export type JobDetail = {
  date_started_time: number;
  date_hours_needed: number;
  venue_name: string;
  client_name: string;
  start_time: string;
  end_time: string;
  id: number;
  job_id: number;
  hours: number;
  date: string;
  orginal_date: Date | null;
  venue_address_line_1: string;
  venue_address_line_2: string;
  venue_address_line_3: string;
  venue_address_postcode: string;
  client_contact_one_name: string;
  client_contact_one_mobile: string;
  client_contact_two_name: string;
  client_contact_two_mobile: string;
  client_date_contact_name: string;
  client_date_contact_mobile: string;
  venue_operational_notes: string;
  venue_crew_notes: string;
  venue_client_notes: string;
  venue_what_three_words: string;
  venue_what_three_words_lat: string | null;
  venue_what_three_words_lng: string | null;
  crew_name: string;
  crew_level: string;
  job_date_out_of_town_bonus_crew: number | null;
  zero_travel_expense: boolean;
  hoursTotal: number;
  experienceBonus: number | null;
  shiftEnd: Date | null;
  shiftStart: Date | null;
  feedbackTimePast: boolean;
  cancelled: boolean;
  crew_cancelled_charge: string;
  cancel_crew_amount: string;
  date_crew_notes: string;
  date_crew_chief_notes: string;
  cancelled_charge: number;
  misc_skills: { name: string, value: string | null, job_crew_id: number, is_custom: boolean, skill: { non_taxible: boolean | null } }[]
}

type RateKeys = `rate${string}` | `london${string}`;

export const mapJobDateCrewDetailForFrontend = (
  jobDateCrewList: JobDatesCrewList,
  options?: { isFlatRate?: boolean }
): JobDetail => {
  if (!jobDateCrewList) throw new Error('Job Date Crew List is required');

  const effectiveHours = jobDateCrewList.adjusted_hours ? Number(jobDateCrewList.date_hours_needed) + Number(jobDateCrewList.adjusted_hours || 0) : Number(jobDateCrewList.date_hours_needed);

  // Use the base hours (date_hours_needed) for rate key lookup to ensure whole number
  const baseHours = Number(jobDateCrewList.date_hours_needed);
  const rateKey: RateKeys = jobDateCrewList.is_london_rate ? `london${baseHours}` : `rate${baseHours}`;
  const baseRate = Number(jobDateCrewList[rateKey as keyof JobDatesCrewList]) || 0;

  // Calculate hourly rate for fractional adjustments
  const hourlyRate = baseHours > 0 ? baseRate / baseHours : 0;

  // Apply adjustment to get final rate for effective hours
  const adjustmentHours = effectiveHours - baseHours;
  const hoursTotal = baseRate + (hourlyRate * adjustmentHours);

  const existingStartDate = new Date(jobDateCrewList.date_started_at);

  const shiftEndDate = existingStartDate && jobDateCrewList.date_hours_needed ? addHours(existingStartDate, jobDateCrewList.date_hours_needed) : null

  // Feedback deadline: 48 hours for flat rate crew, 24 hours for crew chiefs (crew chief takes precedence)
  const now = new Date();
  const feedbackHours = options?.isFlatRate ? 48 : 24;
  const feedbackDeadline = shiftEndDate ? addHours(shiftEndDate, feedbackHours) : null;
  const feedbackTimePast = !existingStartDate || !feedbackDeadline ? false :
    now < existingStartDate || now > feedbackDeadline;

  return {
    id: jobDateCrewList.date_id,
    job_id: jobDateCrewList.job_id,
    date_started_time: jobDateCrewList.date_started_time || 0,
    date_hours_needed: jobDateCrewList.date_hours_needed || 0,
    venue_name: jobDateCrewList.venue_name || '',
    client_name: jobDateCrewList.client_name || '',
    start_time: formatLocalTime(jobDateCrewList.date_started_at, "HH:mm"),
    end_time: formatLocalTime(addHours(jobDateCrewList.date_started_at, jobDateCrewList.date_hours_needed || 0), "HH:mm"),
    hours: jobDateCrewList.date_hours_needed || 0,
    date: jobDateCrewList.date_started_at ? formatDate(jobDateCrewList.date_started_at, 'EEEE dd/MM/yyyy') : '',
    orginal_date: jobDateCrewList.date_started_at,
    venue_address_line_1: jobDateCrewList.venue_address_line_1 || '',
    venue_address_line_2: jobDateCrewList.venue_address_line_2 || '',
    venue_address_line_3: jobDateCrewList.venue_address_line_3 || '',
    venue_address_postcode: jobDateCrewList.venue_address_postcode || '',
    client_contact_one_name: jobDateCrewList.client_contact_one_name || '',
    client_contact_one_mobile: jobDateCrewList.client_contact_one_mobile || '',
    client_contact_two_name: jobDateCrewList.client_contact_two_name || '',
    client_contact_two_mobile: jobDateCrewList.client_contact_two_mobile || '',
    client_date_contact_name: jobDateCrewList.client_date_contact_name || '',
    client_date_contact_mobile: jobDateCrewList.client_date_contact_mobile || '',
    venue_operational_notes: jobDateCrewList.venue_operational_notes || '',
    date_crew_notes: jobDateCrewList.date_crew_notes || '',
    date_crew_chief_notes: jobDateCrewList.date_crew_chief_notes || '',
    venue_crew_notes: jobDateCrewList.venue_crew_notes || '',
    venue_client_notes: jobDateCrewList.venue_client_notes || '',
    venue_what_three_words: jobDateCrewList.venue_what_three_words || '',
    venue_what_three_words_lat: jobDateCrewList.venue_what_three_words_lat?.toString() || null,
    venue_what_three_words_lng: jobDateCrewList.venue_what_three_words_lng?.toString() || null,
    crew_name: jobDateCrewList.crew_name || '',
    crew_level: jobDateCrewList.crew_level_name || '',
    job_date_out_of_town_bonus_crew: jobDateCrewList.job_date_out_of_town_bonus_crew || null,
    zero_travel_expense: (jobDateCrewList as any).zero_travel_expense || false,
    hoursTotal,
    experienceBonus: Number(jobDateCrewList.crew_loyalty_bonus) || null,
    shiftStart: existingStartDate,
    shiftEnd: shiftEndDate,
    feedbackTimePast,
    cancelled: jobDateCrewList.date_day_cancelled || false,
    crew_cancelled_charge: mapDecimalToString(jobDateCrewList.crew_cancelled_charge) || '0',
    cancel_crew_amount: mapDecimalToString(jobDateCrewList.cancel_crew_amount) || '',
    cancelled_charge: jobDateCrewList.cancelled_charge || 0,
    misc_skills: jobDateCrewList.misc_skills?.map((skill) => ({
      name: skill.name,
      value: mapDecimalToString(skill.value),
      job_crew_id: skill.job_crew_id,
      is_custom: skill.is_custom,
      skill: {
        non_taxible: skill?.skill?.non_taxible || null
      }
    })) || []
  };
}


export const getTotal = (jobDetail: JobDetail, crewDetail: CrewList[0] | undefined, extraHoursRate: string | null, getsOotBonus: boolean = true): number => {
  if (!crewDetail) return 0;

  const skillTotal = Number(jobDetail.misc_skills?.reduce((acc, curr) => acc + Number(curr.value || 0), 0)) || 0;

  // Only include out-of-town bonus if the level allows it (gets_oot_bonus is true) and zero_travel_expense is not set
  const ootBonus = (getsOotBonus && !jobDetail.zero_travel_expense) ? Number(jobDetail.job_date_out_of_town_bonus_crew) : 0;

  const misc = ootBonus +
    Number(crewDetail.drive) +
    Number(crewDetail.drive_bonus) +
    Number(crewDetail.chief) +
    skillTotal;

  const extraHours = Number(crewDetail.hours) * Number(extraHoursRate);

  return jobDetail.hoursTotal + misc + extraHours + (jobDetail.experienceBonus || 0);
}

export const getNonTaxableTotal = (jobDetail: JobDetail, crewDetail: CrewList[0] | undefined) => {
  if (!crewDetail) return 0;

  const nonTaxableMisc = jobDetail.misc_skills?.filter((skill) => skill.is_custom || skill.skill.non_taxible === true).reduce((acc, curr) => acc + Number(curr.value || 0), 0) || 0;
  const drive = Number(crewDetail.drive);

  return nonTaxableMisc + drive;
}